import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Experience.css';

const Experience = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const experienceData = [
    {
      id: 1,
      period: '2023 - Present',
      title: 'Senior Software Engineer',
      company: 'TechCorp Solutions',
      location: 'San Francisco, CA',
      description: 'Leading development of scalable web applications serving 100k+ users. Mentoring junior developers and implementing best practices for code quality, testing, and performance optimization. Architected microservices infrastructure that improved system reliability by 40%.',
      achievements: [
        'Led team of 5 developers on major product redesign',
        'Improved application performance by 60%',
        'Implemented CI/CD pipeline reducing deployment time by 80%'
      ]
    },
    {
      id: 2,
      period: '2021 - 2023',
      title: 'Software Engineer',
      company: 'Digital Innovations Ltd.',
      location: 'New York, NY',
      description: 'Developed and maintained full-stack applications for various clients. Collaborated with cross-functional teams to deliver high-quality solutions. Contributed to architectural decisions and technology stack selection for multiple projects.',
      achievements: [
        'Delivered 8 client projects on time and within budget',
        'Reduced bug reports by 45% through comprehensive testing',
        'Mentored 3 junior developers'
      ]
    },
    {
      id: 3,
      period: '2020 - 2021',
      title: 'Junior Developer',
      company: 'StartupHub Inc.',
      location: 'Austin, TX',
      description: 'Built responsive web interfaces and integrated APIs for various client projects. Gained experience in modern development workflows, version control, and agile methodologies. Contributed to open-source projects.',
      achievements: [
        'Completed 15+ frontend projects',
        'Improved mobile responsiveness across all projects',
        'Contributed to 5 open-source repositories'
      ]
    }
  ];

  const educationData = [
    {
      id: 1,
      period: '2016 - 2020',
      degree: 'Bachelor of Science in Computer Science',
      school: 'University of California, Berkeley',
      location: 'Berkeley, CA',
      gpa: '3.8/4.0',
      achievements: [
        'Magna Cum Laude graduate',
        'Dean\'s List for 6 semesters',
        'President of Computer Science Society'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="experience section" id="experience" ref={ref}>
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="section__header" variants={itemVariants}>
            <h2 className="section__title">Experience & Education</h2>
            <p className="section__subtitle">My professional journey</p>
          </motion.div>

          <div className="experience__content">
            <div className="experience__section">
              <motion.h3 className="experience__section-title" variants={itemVariants}>
                <i className="fas fa-briefcase"></i>
                Professional Experience
              </motion.h3>
              
              <div className="timeline">
                {experienceData.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="timeline__item"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="timeline__marker">
                      <div className="timeline__marker-inner"></div>
                    </div>
                    <div className="timeline__content">
                      <div className="timeline__header">
                        <span className="timeline__period">{item.period}</span>
                        <div className="timeline__main">
                          <h4 className="timeline__title">{item.title}</h4>
                          <h5 className="timeline__company">
                            {item.company} • {item.location}
                          </h5>
                        </div>
                      </div>
                      <p className="timeline__description">{item.description}</p>
                      
                      
                      <div className="timeline__achievements">
                        <h6>Key Achievements:</h6>
                        <ul>
                          {item.achievements.map((achievement, idx) => (
                            <li key={idx}>{achievement}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="experience__section">
              <motion.h3 className="experience__section-title" variants={itemVariants}>
                <i className="fas fa-graduation-cap"></i>
                Education
              </motion.h3>
              
              <div className="education__grid">
                {educationData.map((item) => (
                  <motion.div
                    key={item.id}
                    className="education__card"
                    variants={itemVariants}
                    whileHover={{ y: -5, scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="education__header">
                      <span className="education__period">{item.period}</span>
                      <span className="education__gpa">GPA: {item.gpa}</span>
                    </div>
                    <h4 className="education__degree">{item.degree}</h4>
                    <h5 className="education__school">
                      {item.school} • {item.location}
                    </h5>
                    <div className="education__achievements">
                      <h6>Achievements:</h6>
                      <ul>
                        {item.achievements.map((achievement, idx) => (
                          <li key={idx}>{achievement}</li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;